import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { useT } from "../context/LanguageContext";

const DISMISS_KEY = "v2_banner_dismissed_v1";

/**
 * V2 BETA discovery banner.
 * - Reads its rollout state from /api/feature-flags (admin can toggle without redeploy).
 * - Stores a localStorage dismissal so users don't see it again after they X it out.
 * - First banner on the v1 homepage, above the league widgets.
 * - Localized: Arabic users see Arabic copy automatically. Admin overrides
 *   from the feature-flags endpoint apply only to the English locale.
 */
export default function V2Banner() {
  const { t, lang } = useT();
  const [flags, setFlags] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");

  useEffect(() => {
    let cancelled = false;
    api.get("/feature-flags")
      .then(({ data }) => { if (!cancelled) setFlags(data); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  if (dismissed || !flags || !flags.v2_banner_enabled) return null;

  const dismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const isAr = lang === "ar";
  // Arabic locale always uses the translation keys (so the admin's English
  // overrides don't bleed into the AR experience). English locale uses the
  // admin-set text from the flags endpoint.
  const headline = isAr ? t("home.v2Banner.headline") : flags.v2_banner_headline;
  const body = isAr ? t("home.v2Banner.body") : flags.v2_banner_body;
  const cta = isAr ? t("home.v2Banner.cta") : (flags.v2_banner_cta || "Try v2 →");
  const beta = t("home.v2Banner.beta");

  return (
    <Link
      to={flags.v2_banner_target || "/v2"}
      data-testid="v2-banner"
      className="group relative block overflow-hidden rounded-none sm:rounded-2xl mb-6 bg-gradient-to-r from-[#7C3AED] via-[#6D28D9] to-[#3B82F6] hover:shadow-2xl hover:shadow-[#7C3AED]/30 transition-shadow"
    >
      {/* Frosted blob */}
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10 blur-3xl pointer-events-none" />
      {/* Diagonal stripes */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(135deg,#fff 0,#fff 1px,transparent 1px,transparent 14px)",
        }}
        aria-hidden
      />

      <div className="relative flex items-center gap-4 p-4 sm:p-5">
        <div className="hidden sm:flex w-12 h-12 rounded-2xl bg-white/15 backdrop-blur items-center justify-center flex-shrink-0">
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center gap-1 bg-white text-[#7C3AED] font-mono text-[10px] font-bold uppercase tracking-[0.25em] px-2 py-0.5 rounded-full"
              data-testid="v2-banner-pill"
            >
              <Sparkles className="w-2.5 h-2.5" /> {beta}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/80">
              {headline}
            </span>
          </div>
          <p className="text-sm sm:text-base text-white mt-1 leading-snug font-medium" data-testid="v2-banner-body">
            {body}
          </p>
        </div>
        <div
          className="hidden sm:inline-flex items-center gap-1.5 bg-white/20 group-hover:bg-white/30 backdrop-blur rounded-2xl px-4 py-2 text-sm font-bold text-white transition-colors flex-shrink-0"
          data-testid="v2-banner-cta"
        >
          {cta}
          <ChevronRight className={`w-4 h-4 ${isAr ? "rotate-180" : ""}`} />
        </div>
        <button
          onClick={dismiss}
          data-testid="v2-banner-dismiss"
          aria-label={t("home.v2Banner.dismiss")}
          className="ml-1 sm:ml-0 w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/15 text-white/80 hover:text-white flex-shrink-0 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Mobile: full-width CTA below */}
      <div className="sm:hidden border-t border-white/15 px-4 py-2.5 flex items-center justify-center gap-1 text-xs font-bold text-white" data-testid="v2-banner-cta-mobile">
        {cta}
        <ChevronRight className={`w-3 h-3 ${isAr ? "rotate-180" : ""}`} />
      </div>
    </Link>
  );
}
